import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { PageEvent } from '@angular/material/paginator';
import { HttpParameter } from 'src/app/types/http-parameter';
import moment from 'moment';

@Component({
  selector: 'app-payments-made-misc',
  templateUrl: './payments-made-misc.component.html',
  styleUrls: ['./payments-made-misc.component.css']
})
export class PaymentsMadeMiscComponent extends ViewTableComponent implements OnInit {

  currentPage: number;

  dateRange: Date[] = [];

  /**
   * Rows fetched per page by default: enough to cover the whole year-to-date
   * window in one page, so the report lists every payment its date range
   * matched instead of the first ten.
   *
   * Bounded rather than unlimited on purpose. items_per_page=0 (the backend's
   * no-range escape hatch, used by the CSV export) exhausts PHP mid-response on
   * a wide range and resolves as a 200 with an empty body; a year of payments
   * sits far inside this cap.
   */
  private static readonly DEFAULT_PAGE_SIZE = 5000;

  /**
   * The default window: January 1st of the studio's current year through the
   * studio's today (year to date).
   *
   * Without a date filter the view returns every payment ever recorded, and the
   * server-side pager then showed only the first page of it. Both ends are
   * resolved in the studio's timezone, never the browser's, so a studio east or
   * west of the viewer doesn't get a window off by a day, and on Jan 1st, off
   * by a whole year.
   */
  private defaultRangeStart(): Date {
    return moment(this.reportDateService.currentStudioDateKey(), 'YYYY-MM-DD', true)
      .startOf('year').toDate();
  }

  private defaultRangeEnd(): Date {
    return this.reportDateService.studioTodayStart();
  }

  override displayedColumns: string[] = [
    'id',
    'field_date_and_time',
    'field_student_name',
    'field_student_department',
    'field_enrollment_package_name_label',
    // 'AMOUNTPAID',
    'field_payment_type_label',
    'field_instructor_label',
    'closer',
  ];

  override ngOnInit() {
    // Initialize pagination values
    this.paginationIndex = 0;
    this.pageSize = PaymentsMadeMiscComponent.DEFAULT_PAGE_SIZE;

    // Call parent ngOnInit if it exists
    super.ngOnInit();

    // Show the default range in the picker, so the user can see (and widen)
    // the window the figures below actually cover.
    this.applyDefaultDateRange();

    // Initial data load with pagination parameters
    this.getData([
      { parameter: 'page', value: this.paginationIndex.toString() },
      { parameter: 'items_per_page', value: this.pageSize.toString() }
    ]);
  }

  /**
   * Seed the picker with year to date: Jan 1st through today. The owl picker
   * binds to dateRange rather than the form, so this is a plain assignment:
   * getData() picks the window up through withDateRange().
   */
  private applyDefaultDateRange(): void {
    this.dateRange = [this.defaultRangeStart(), this.defaultRangeEnd()];
  }

  /**
   * Push the owl-picked range through getData as start_date/end_date, which
   * withDateRange() converts into the field_date_and_time contextual param.
   */
  dateRangeChange(event: any): void {
    if (!event?.value || !Array.isArray(event.value)) {
      return;
    }
    this.dateRange = event.value;
    const params: HttpParameter[] = [];
    if (this.dateRange[0] && this.dateRange[1]) {
      params.push(
        { parameter: 'start_date', value: moment(this.dateRange[0]).format('YYYY-MM-DD') },
        { parameter: 'end_date', value: moment(this.dateRange[1]).format('YYYY-MM-DD') },
      );
    }
    this.getData(params);
  }

  /**
   * Translate the start_date/end_date pickers into the single `from--to`
   * contextual range param the cash_reports view expects, falling back to the
   * default window when the user has cleared one or both ends.
   */
  private withDateRange(params: { parameter: string; value: string; }[]) {
    const find = (name: string) =>
      params.find(p => p.parameter === name)?.value;

    const start = this.reportDateService.formatCalendarParam(
      find('start_date') ?? this.dateRange?.[0] ?? this.defaultRangeStart());
    const end = this.reportDateService.formatCalendarParam(
      find('end_date') ?? this.dateRange?.[1] ?? this.defaultRangeEnd());

    if (!start || !end) {
      return params;
    }

    // Drop the picker params themselves: the view only understands the
    // combined range argument.
    return [
      ...params.filter(p =>
        p.parameter !== 'start_date' && p.parameter !== 'end_date'),
      { parameter: 'field_date_and_time', value: `${start}--${end}` },
    ];
  }

  override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;

    // Store only non-pagination params in queryParams
    this.queryParams = params.filter(param =>
      param.parameter !== 'page' && param.parameter !== 'items_per_page'
    );

    // Add current pagination state to params if not present. Checked
    // independently: updateRouterParams() injects page=0 into every navigation
    // that lacks it, so a combined guard left items_per_page off the request
    // and the backend fell back to its own default of 10 rows while still
    // reporting the full count.
    let finalParams = [...params];
    if (!params.some(p => p.parameter === 'page')) {
      finalParams.push({ parameter: 'page', value: this.paginationIndex.toString() });
    }
    if (!params.some(p => p.parameter === 'items_per_page')) {
      finalParams.push({ parameter: 'items_per_page', value: this.pageSize.toString() });
    }

    // Add sorting parameters if available
    if (this.sortParams !== undefined) {
      finalParams.push(this.sortParams);
    }

    finalParams = this.withDateRange(finalParams);

    // Load the new data with the filtering parameters.
    let endpoint: string = "/api/v1/reports/cash?field_sps_code=554";

    this._drupalRESTService.httpGET(endpoint, finalParams)
      .subscribe((data) => {
        // ShowProgressBar
        this.ShowProgressBar = false;

        this.dataSource = new MatTableDataSource(data['results']);

        // Pagination
        if (data['results']) {
          this.setupPagination(data, params);
        }
      },
        error => this.handleError(error)
      )
  }

  override resetForm() {
    // Clear all stored params
    this.queryParams = [];
    this.sortParams = undefined;

    // Reset pagination
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginationIndex = 0;  // Reset the pagination index
      this.pageSize = PaymentsMadeMiscComponent.DEFAULT_PAGE_SIZE;
    }

    // Clear the form, then put the default window back so Clear returns to the
    // same view the report opens with rather than an unbounded one.
    this.form.resetForm();
    this.applyDefaultDateRange();


    // Clear any autocomplete options if they exist
    if (this.autoCompleteEnrollmentOptions) {
      this.autoCompleteEnrollmentOptions = [];
    }

    // Make a fresh request with explicitly empty params
    this.getData([
      { parameter: 'page', value: '0' },
      { parameter: 'items_per_page', value: PaymentsMadeMiscComponent.DEFAULT_PAGE_SIZE.toString() }
    ]);

    // Update router last
    this.updateRouterParams({});
  }

  override pageChanged(event: PageEvent) {
    // Update pagination state
    this.paginationIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    // Always include pagination parameters
    const paginationParams: HttpParameter[] = [
      { parameter: 'page', value: this.paginationIndex.toString() },
      { parameter: 'items_per_page', value: this.pageSize.toString() }
    ];

    // Filter out any existing pagination params from queryParams
    const filteredQueryParams = this.queryParams.filter(param =>
      param.parameter !== 'page' && param.parameter !== 'items_per_page'
    );

    // Combine filtered query params with new pagination params
    const combinedParams = [...filteredQueryParams, ...paginationParams];
    this.getData(combinedParams);
  }

  override onPageSizeOptions() {
    // The last option is the show-everything page (see DEFAULT_PAGE_SIZE).
    return [10, 25, 50, 100, 250, PaymentsMadeMiscComponent.DEFAULT_PAGE_SIZE];
  }

  override setupPagination(data: any, params: { parameter: string; value: string; }[]) {
    const pageParam = params.find(param => param.parameter === 'page');
    if (this.paginator) {
      this.paginator.length = data?.['count'] ?? 0;
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageIndex = pageParam ? parseInt(pageParam.value, 10) : 0;
    }
    // DO NOT call updateRouterParams here - prevents duplicate requests
  }
}
